import * as Clipboard from 'expo-clipboard';
import { useEffect, useRef, useState } from 'react';
import { Modal, TouchableOpacity, StyleSheet, TextInput } from 'react-native';
import { useToast } from 'react-native-toast-notifications';

import Icon from '@components/shared/Icon/RestyleIcon';
import { useCreateInviteMutation } from '@graphql/generated';

import { Box, Text } from '../../components/Restyle';

import type React from 'react';

interface ShareModalProps {
  visible: boolean;
  onClose: () => void;
  shareLink?: string;
  title?: string;
  subtitle?: string;
  projectId?: string;
}

const ShareModal: React.FC<ShareModalProps> = ({
  visible,
  onClose,
  shareLink = '',
  title = 'Invite Members',
  subtitle = 'Bring together the perfect team in just a few clicks.',
  projectId,
}) => {
  const toast = useToast();
  const [link, setLink] = useState<string>('');
  // Tracks whether we've already generated an invite for the current open
  // cycle so opening/closing the modal repeatedly doesn't issue a new
  // createInvite (and create a new DB token) each time. Matches the pattern
  // used in ProjectLinkInviteBottomSheet.
  const didCreateInviteRef = useRef(false);
  // Without projectId the backend falls back to create_user_invite_link, which
  // emits a URL without a `type` parameter. InvitedUser.tsx then defaults the
  // link type to "team", routing the invitee to the team preview screen where
  // they see "Team Not Found" (no real team exists yet for a user who only has
  // the personal-projects team). Scoping the invite to a project keeps the
  // link on the project-invitation-preview path. See TA-9533.
  const [createInviteMutation, { data: createInviteData }] =
    useCreateInviteMutation();

  useEffect(() => {
    if (!createInviteData) return;
    setLink(
      (createInviteData?.createInvite ?? '').replace(
        'register/register-with-email',
        'invited-user'
      )
    );
  }, [createInviteData]);

  useEffect(() => {
    // When the modal closes, reset so the next open cycle can generate a
    // fresh invite. Also clear the displayed link so a stale value doesn't
    // flash when the modal is reopened.
    if (!visible) {
      didCreateInviteRef.current = false;
      setLink('');
      return;
    }
    // Require projectId before generating the invite. Calling createInvite
    // with no arguments falls through to the backend's create_user_invite_link
    // path, which emits a typeless URL that /invited-user then mis-routes to
    // the team preview (TA-9533). The effect re-runs once projectId becomes
    // defined.
    if (!projectId || didCreateInviteRef.current) return;
    didCreateInviteRef.current = true;
    createInviteMutation({
      variables: { projectId },
    });
  }, [visible, projectId, createInviteMutation]);

  const copyToClipboard = async (link: string) => {
    toast.show('Copied', {
      duration: 2000,
    });

    await Clipboard.setStringAsync(link || '');
  };

  return (
    <Modal
      animationType='fade'
      transparent={true}
      visible={visible}
      onRequestClose={() => {}}>
      <Box style={styles.centeredView}>
        <Box style={styles.modalView}>
          <TouchableOpacity style={styles.closeButton} onPress={onClose}>
            <Icon
              backgroundColor='transparent'
              name='XCircleTrans'
              color='grey04'
              variant='l'
            />
          </TouchableOpacity>

          <Text style={styles.title}>{title}</Text>
          <Text style={styles.subtitle}>{subtitle}</Text>

          <Box style={styles.shareSection}>
            <Text style={styles.sectionTitle}>Share read-only link</Text>

            <Box style={styles.linkContainer}>
              <TextInput
                style={styles.linkInput}
                value={link ?? shareLink}
                editable={false}
              />
              <TouchableOpacity
                style={styles.copyButton}
                onPress={() =>
                  copyToClipboard(
                    (link ?? '').replace(
                      'register/register-with-email',
                      'invited-user'
                    )
                  )
                }>
                <Text style={styles.copyButtonText}>Copy link</Text>
              </TouchableOpacity>
            </Box>

            <Text style={styles.helperText}>
              You can copy link and share it with your mates using your favorite
              apps like WhatsApp, Facebook, Email etc.
            </Text>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

const styles = StyleSheet.create({
  centeredView: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.5)', // Dimmed background
  },
  modalView: {
    width: '90%',
    maxWidth: 500,
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  closeButton: {
    position: 'absolute',
    right: 16,
    top: 16,
    zIndex: 1,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 8,
  },
  subtitle: {
    fontSize: 16,
    textAlign: 'center',
    color: '#555',
    marginBottom: 32,
  },
  shareSection: {
    marginTop: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 16,
  },
  linkContainer: {
    flexDirection: 'row',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    overflow: 'hidden',
    marginBottom: 16,
  },
  linkInput: {
    flex: 1,
    padding: 12,
    backgroundColor: '#f9f9f9',
    color: '#666',
  },
  copyButton: {
    backgroundColor: '#000',
    paddingHorizontal: 16,
    justifyContent: 'center',
    alignItems: 'center',
  },
  copyButtonText: {
    color: 'white',
    fontWeight: '600',
  },
  helperText: {
    fontSize: 14,
    color: '#666',
    lineHeight: 20,
  },
});

export default ShareModal;
